// Plan volume estimator.
//
// Entirely client-side over data already published on the page: there is no
// endpoint, no request, and nothing submitted, so there is no input to
// validate server-side and no abuse surface to rate-limit. It highlights a
// plan; it never creates anything.
//
// Vendors think in sheets per week, not square feet per month, so the input
// takes sheets and converts. WEEKS_PER_MONTH is 52/12 rather than 4, because 4
// undercounts by ~8% and this must never suggest a plan that is too small.
const WEEKS_PER_MONTH = 52 / 12

export function suggestPlan(plans, monthlySquareFeet) {
  if (!Array.isArray(plans) || plans.length === 0) return null
  if (!(monthlySquareFeet > 0)) return null

  const bySize = [...plans].sort((a, b) => a.sqft - b.sqft)
  return bySize.find(plan => plan.sqft >= monthlySquareFeet) || null
}

export function monthlySquareFeetFor(sheetsPerWeek, squareFeetPerSheet) {
  if (!(sheetsPerWeek > 0) || !(squareFeetPerSheet > 0)) return 0
  return sheetsPerWeek * squareFeetPerSheet * WEEKS_PER_MONTH
}

function initPlanEstimator() {
  const root = document.querySelector('[data-plan-estimator]')
  if (!root) return

  let plans = []
  try {
    plans = JSON.parse(root.dataset.plans || '[]')
  } catch {
    return
  }

  const sheetsInput = root.querySelector('[data-estimator-sheets]')
  const sizeSelect = root.querySelector('[data-estimator-size]')
  const result = root.querySelector('[data-estimator-result]')
  if (!sheetsInput || !sizeSelect || !result) return

  const update = () => {
    const sheets = Number.parseFloat(sheetsInput.value)
    const perSheet = Number.parseFloat(sizeSelect.value)
    const monthly = monthlySquareFeetFor(sheets, perSheet)

    if (monthly <= 0) {
      result.textContent = 'Enter your weekly sheets and we will suggest a plan.'
      return
    }

    const rounded = Math.round(monthly).toLocaleString()
    const plan = suggestPlan(plans, monthly)

    result.textContent = plan
      ? `About ${rounded} sq ft a month — ${plan.name} covers that at $${plan.price}/mo.`
      : `About ${rounded} sq ft a month — that is beyond our largest plan. Get in touch and we will size one to your volume.`
  }

  sheetsInput.addEventListener('input', update)
  sizeSelect.addEventListener('change', update)
}

document.addEventListener('DOMContentLoaded', initPlanEstimator)

// Landing scroll reveals — CSP-compliant (script-src :self), no library.
document.addEventListener('DOMContentLoaded', () => {
  const revealables = document.querySelectorAll('[data-reveal]')
  if (!('IntersectionObserver' in window) || revealables.length === 0) {
    revealables.forEach(el => el.classList.add('is-visible'))
    return
  }
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return
      entry.target.classList.add('is-visible')
      observer.unobserve(entry.target)
    })
  }, { threshold: 0.15 })
  revealables.forEach(el => observer.observe(el))
})
